import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AssignEmployeToTaskWhenServiceStarts from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/AssignEmployeeToTaskWhenServiceStarts";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import { getUserJobs, getUserServices } from "../../services/analytics";
import { term } from "../../terms";
import MyAssignedJobsTable from "./MyAssignedJobsTable";
import MyAssignedServicesTable from "./MyAssignedServicesTable";
import { usePermissions } from "../../context/PermissionsContext";

const ASSIGNMENTS = [
    { label: `${term("step_plural")}`, value: 1 },
    { label: "Services", value: 2 },
];

function MyAssignments() {
    const { can, permissionsLoading } = usePermissions();
    const canReadServices = can("client_services.read");
    const canWriteServices = can("client_services.write");
    const canReadSteps = can("steps.read");
    const availableAssignments = useMemo(
        () =>
            ASSIGNMENTS.filter((assignment) =>
                assignment.value === 1 ? canReadSteps : canReadServices,
            ),
        [canReadServices, canReadSteps],
    );
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState(null);
    const [services, setServices] = useState(null);
    const [employeeName, setEmployeeName] = useState("");
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [isModalStartServiceOpen, setIsModalStartServiceOpen] = useState(false);
    const [clientServiceId, setClientServiceId] = useState(null);
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [clientId, setClientId] = useState(null);
    const params = useParams();
    const shouldRefreshAssignments = location.state?.refresh === true;

    const fetchMyJobs = useCallback(async () => {
        if (!canReadSteps) {
            setJobs([]);
            setLoading(false);
            return;
        }
        const response = await getUserJobs();

        if (response.success) {
            setJobs(response.data.tasksAssignedToUser);
            setEmployeeName(response.data.fullName);
        } else {
            toast.error("Error fetching steps.");
        }

        setLoading(false);
    }, [canReadSteps]);

    const fetchMyServices = useCallback(async () => {
        if (!canReadServices) {
            setServices([]);
            setLoading(false);
            return;
        }
        const response = await getUserServices();

        if (response.success) {
            setServices(response.data.servicesAssignedToUser);
            setEmployeeName(response.data.fullName);
        } else {
            toast.error("Error fetching services.");
        }

        setLoading(false);
    }, [canReadServices]);

    const getAssignmentFromParams = useCallback(() => {
        switch (params.filterName) {
            case "jobs":
                return canReadSteps
                    ? ASSIGNMENTS[0]
                    : availableAssignments[0] || null;
            case "services":
            default:
                return canReadServices
                    ? ASSIGNMENTS[1]
                    : availableAssignments[0] || null;
        }
    }, [
        params.filterName,
        canReadServices,
        canReadSteps,
        availableAssignments,
    ]);

    const loadAssignment = useCallback(
        async (assignment) => {
            if (permissionsLoading || !assignment) {
                setSelectedAssignment(null);
                setLoading(false);
                return;
            }
            setLoading(true);

            if (assignment.label === ASSIGNMENTS[0].label) {
                await fetchMyJobs();
            } else {
                await fetchMyServices();
            }

            setSelectedAssignment(assignment);
        },
        [fetchMyJobs, fetchMyServices, permissionsLoading],
    );

    const onChangeAssignment = async (assignment) => {
        await loadAssignment(assignment);
    };

    useEffect(() => {
        getNewLoggedInEmployee();
    }, []);

    useEffect(() => {
        if (shouldRefreshAssignments) {
            return;
        }

        loadAssignment(getAssignmentFromParams());
    }, [getAssignmentFromParams, loadAssignment, shouldRefreshAssignments]);

    useEffect(() => {
        if (!shouldRefreshAssignments) {
            return;
        }

        const refreshAssignments = async () => {
            await loadAssignment(getAssignmentFromParams());
            navigate(location.pathname, { replace: true, state: {} });
        };

        refreshAssignments();
    }, [
        getAssignmentFromParams,
        loadAssignment,
        location.pathname,
        navigate,
        shouldRefreshAssignments,
    ]);

    const onHandleStartService = (nextClientServiceId, nextClientId) => {
        setClientId(nextClientId);
        setIsModalStartServiceOpen(true);
        setClientServiceId(nextClientServiceId);
    };

    const getNewLoggedInEmployee = async () => {
        const currentEmployee = await getLoggedInEmployee();
        if (currentEmployee.success) {
            setLoggedInEmployee(currentEmployee.data.employeeResponse);
        }
    };

    return (
        <div className="flex h-screen overflow-hidden">
            {canWriteServices && loggedInEmployee && clientId && (
                <AssignEmployeToTaskWhenServiceStarts
                    open={isModalStartServiceOpen}
                    setOpen={setIsModalStartServiceOpen}
                    loggedInEmployee={loggedInEmployee}
                    clientSubscriptionId={clientServiceId}
                    newClientService={fetchMyServices}
                    clientId={clientId}
                />
            )}
            <Toaster />

            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

            <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                {!permissionsLoading && !loading ? (
                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="mb-4 sm:mb-0">
                                <button
                                    onClick={() => window.history.back()}
                                    className="flex items-center text-slate-800 hover:text-slate-600"
                                >
                                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                                    Return
                                </button>
                            </div>

                            <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                <div className="mb-4 sm:mb-0">
                                    <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                        {employeeName} Assignments
                                    </h1>
                                </div>

                                <div className="mb-4 sm:mb-0">
                                    <h2 className="text-2xl font-bold md:text-3xl text-slate-800">
                                        {selectedAssignment?.label}
                                    </h2>
                                </div>

                                <div className="flex flex-col items-start">
                                    <h2 className="font-bold text-slate-800">
                                        Select Assignment
                                    </h2>
                                    <Select
                                        onChange={onChangeAssignment}
                                        className="basic-single min-w-[180px] w-auto"
                                        classNamePrefix="select"
                                        isSearchable={true}
                                        value={selectedAssignment}
                                        options={availableAssignments}
                                        name="assignments"
                                    />
                                </div>
                            </div>

                            {selectedAssignment?.label === ASSIGNMENTS[0].label && (
                                <MyAssignedJobsTable
                                    data={jobs}
                                    employeeName={employeeName}
                                    canNavigateToService={canReadServices}
                                    canReadSteps={canReadSteps}
                                />
                            )}

                            {selectedAssignment?.label === ASSIGNMENTS[1].label && (
                                <MyAssignedServicesTable
                                    data={services}
                                    employeeName={employeeName}
                                    onHandleUpdateServiceStatus={
                                        onHandleStartService
                                    }
                                    canWriteServices={canWriteServices}
                                    canReadServices={canReadServices}
                                />
                            )}
                            {!selectedAssignment && (
                                <div className="p-4 text-sm bg-white border rounded-sm text-slate-500 border-slate-200">
                                    You don't have permission to view assignments.
                                </div>
                            )}
                        </div>
                    </main>
                ) : (
                    <div className="flex items-center justify-center w-full mt-64 max-w-9xl">
                        <motion.div
                            className="border border-t-gray-700 border-t-[4px] rounded-full border-gray-200 w-12 h-12"
                            initial={{ rotateZ: 0 }}
                            animate={{ rotateZ: 360 }}
                            exit={{ rotateZ: 0 }}
                            transition={{
                                repeat: Infinity,
                                ease: "linear",
                                duration: 1,
                            }}
                        ></motion.div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MyAssignments;
